import { useState, useEffect, useCallback } from 'react'
import { Popup } from 'devextreme-react/popup'

type UseButtonTriggerOptions = {
	selector: string
	triggerKeys: string | string[]
	onBeforeTrigger?: (event: KeyboardEvent) => void | boolean
	onAfterTrigger?: () => void
}

export function useButtonTrigger(options: UseButtonTriggerOptions) {
	const { selector, triggerKeys, onBeforeTrigger, onAfterTrigger } = options

	const handleKeyPress = useCallback(
		(event: KeyboardEvent) => {
			const keys = Array.isArray(triggerKeys) ? triggerKeys : [triggerKeys]
			if (keys.includes(event.key)) {
				if (onBeforeTrigger && onBeforeTrigger(event) === false) {
					return
				}
				const element = document.querySelector(selector) as HTMLElement | null
				if (element) {
					const clickEvent = new MouseEvent('click', {
						bubbles: true,
						cancelable: true,
						view: window,
					})
					element.dispatchEvent(clickEvent)
					if (onAfterTrigger) {
						onAfterTrigger()
					}
				} else {
					console.warn(`Element not found for selector: "${selector}"`)
				}
			}
		},
		[selector, triggerKeys, onBeforeTrigger, onAfterTrigger]
	)

	useEffect(() => {
		window.addEventListener('keydown', handleKeyPress)
		return () => {
			window.removeEventListener('keydown', handleKeyPress)
		}
	}, [handleKeyPress])
}

type UseModelHotkeysProps = {
	filterPopupVisible: boolean
	buttonPopupShow: boolean
	showAuditTrail: boolean
	showRow: boolean
	showMap: boolean
}

export function useModelHotkeys({
	filterPopupVisible,
	buttonPopupShow,
	showAuditTrail,
	showRow,
	showMap,
}: UseModelHotkeysProps) {
	const [hotkeyHelpVisible, setHotkeyHelpVisible] = useState(false)

	const isFormFocused = useCallback(() => {
		const active = document.activeElement
		if (!active) return true
		const tag = active.tagName.toLowerCase()
		if (tag === 'input' || tag === 'textarea' || (active as HTMLElement).isContentEditable) return false
		if (filterPopupVisible || buttonPopupShow || showAuditTrail || showRow || showMap || hotkeyHelpVisible)
			return false
		return true
	}, [filterPopupVisible, buttonPopupShow, showAuditTrail, showRow, showMap, hotkeyHelpVisible])

	// Open Filter Builder: Alt+F
	useButtonTrigger({
		selector: '.dx-datagrid-filter-panel-text',
		triggerKeys: ['f', 'F'],
		onBeforeTrigger: e => {
			if (e.altKey && !e.ctrlKey && !e.shiftKey && isFormFocused()) {
				e.preventDefault()
				return true
			}
			return false
		},
	})

	// Open Hotkey Help: Alt+/
	useEffect(() => {
		const handler = (e: KeyboardEvent) => {
			if (!e.ctrlKey && !e.shiftKey && e.altKey && e.key === '/' && isFormFocused()) {
				e.preventDefault()
				setHotkeyHelpVisible(true)
			}
		}
		window.addEventListener('keydown', handler)
		return () => window.removeEventListener('keydown', handler)
	}, [isFormFocused])

	// Clear All Filters: Alt+Shift+C
	useButtonTrigger({
		selector: 'div.dx-button[role="button"][title="Clear Filter"]',
		triggerKeys: ['c', 'C'],
		onBeforeTrigger: e => {
			if (e.altKey && !e.ctrlKey && e.shiftKey && isFormFocused()) {
				e.preventDefault()
				return true
			}
			return false
		},
	})

	// Apply Filter: Enter
	useButtonTrigger({
		selector: '.dx-toolbar-item[class*="applyFilter"] .dx-button',
		triggerKeys: 'Enter',
		onBeforeTrigger: () => {
			const button = document.querySelector('.dx-toolbar-item[class*="applyFilter"] .dx-button')
			return !button?.classList.contains('dx-state-disabled')
		},
	})

	// Refresh Form Data: Alt+R
	useButtonTrigger({
		selector: 'div.dx-button[role="button"][title="Refresh"]',
		triggerKeys: ['r', 'R'],
		onBeforeTrigger: e => {
			if (e.altKey && !e.ctrlKey && !e.shiftKey && isFormFocused()) {
				e.preventDefault()
				return true
			}
			return false
		},
	})

	// Toggle Edit Mode: Alt+E
	useButtonTrigger({
		selector: 'div.dx-button[role="button"][title="Edit Mode"]',
		triggerKeys: ['e', 'E'],
		onBeforeTrigger: e => {
			if (e.altKey && !e.ctrlKey && !e.shiftKey && isFormFocused()) {
				e.preventDefault()
				return true
			}
			return false
		},
	})

	// Add New Row: Alt+N
	useButtonTrigger({
		selector: '.dx-toolbar .dx-button[aria-label="Add a row"]',
		triggerKeys: ['n', 'N'],
		onBeforeTrigger: e => {
			if (e.altKey && !e.ctrlKey && !e.shiftKey && isFormFocused()) {
				e.preventDefault()
				return true
			}
			return false
		},
	})

	// Toggle Edit Mode: Alt+S
	useButtonTrigger({
		selector: 'div.dx-button[role="button"][title="Save"]',
		triggerKeys: ['s', 'S'],
		onBeforeTrigger: e => {
			if (e.altKey && !e.ctrlKey && !e.shiftKey && isFormFocused()) {
				e.preventDefault()
				return true
			}
			return false
		},
	})

	const hotkeyHelpModal = (
		<Popup
			visible={hotkeyHelpVisible}
			showTitle={true}
			title="Keyboard Shortcuts"
			onHiding={() => setHotkeyHelpVisible(false)}
			defaultWidth="400px"
			defaultHeight="auto"
			hideOnOutsideClick={true}
			resizeEnabled={false}
		>
			<div style={{ padding: 16 }}>
				<table style={{ width: '100%', borderCollapse: 'collapse' }}>
					<thead>
						<tr>
							<th style={{ textAlign: 'left', borderBottom: '1px solid #ccc' }}>Action</th>
							<th style={{ textAlign: 'left', borderBottom: '1px solid #ccc' }}>Hotkey</th>
						</tr>
					</thead>
					<tbody>
						<tr>
							<td>Apply Filter</td>
							<td>Enter</td>
						</tr>
						<tr>
							<td>Open Filter Builder</td>
							<td>Alt + F</td>
						</tr>
						<tr>
							<td>Clear All Filters</td>
							<td>Alt + Shift + C</td>
						</tr>
						<tr>
							<td>Refresh Form Data</td>
							<td>Alt + R</td>
						</tr>
						<tr>
							<td>Toggle Edit Mode</td>
							<td>Alt + E</td>
						</tr>
						<tr>
							<td>Add New Row</td>
							<td>Alt + N</td>
						</tr>
						<tr>
							<td>Save Changes</td>
							<td>Alt + S</td>
						</tr>
						<tr>
							<td>Open Hotkey Help</td>
							<td>Alt + /</td>
						</tr>
					</tbody>
				</table>
				<div style={{ marginTop: 16, fontSize: 12, color: '#888' }}>
					Shortcuts work when the form is active, and no fields or pop-ups are open. Only supported with
					English keyboard layout.
				</div>
			</div>
		</Popup>
	)

	return { hotkeyHelpModal }
}
